import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import BasicInput from './BasicInput';
import Select from './Select';
import closeIcon from '@/assets/close.svg';
import avatarIcon from '@/assets/avatar.svg';
import ChangePasswordDialog from './ChangePasswordDialog';
import ChangeEmailDialog from './ChangeEmailDialog';
import BindPhoneDialog from './BindPhoneDialog';
import { validateAvatar, getAvatarUrl, uploadFile } from '@/utils/imageUtils';
import { validateNickname } from '@/utils/validationUtils';

const ProfileEditor = ({ 
  onClose, 
  onSave, 
  profileData = null
}) => {
  const { t } = useTranslation();
  
  const genderOptions = [
    { value: 'UNSPECIFIED', label: t('user.gender.unspecified') },
    { value: 'MALE', label: t('user.gender.male') },
    { value: 'FEMALE', label: t('user.gender.female') },
  ];
  
  const [formData, setFormData] = useState({
    nickname: '',
    avatar: null,
    birthday: '',
    gender: 'UNSPECIFIED'
  });
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [isBindingPhone, setIsBindingPhone] = useState(false);

  // Initialize form data with profile data when component mounts or profileData changes
  useEffect(() => {
    if (profileData) {
      setFormData({
        nickname: profileData.nickname || '',
        avatar: profileData.avatar || null,
        birthday: profileData.birthday || '',
        gender: profileData.gender || 'UNSPECIFIED'
      });
    }
  }, [profileData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate nickname
    if (!formData.nickname.trim()) {
      newErrors.nickname = t('user.errors.nickname_required');
      isValid = false;
    } else if (!validateNickname(formData.nickname)) {
      newErrors.nickname = t('user.errors.nickname_invalid');
      isValid = false;
    }

    // Validate birthday
    if (formData.birthday) {
      const birthDate = new Date(formData.birthday);
      const birthYear = birthDate.getFullYear();
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const maxYear = currentYear - 18;

      if (birthYear < 1900) {
        newErrors.birthday = t('user.errors.birthday_too_early');
        isValid = false;
      } else if (birthYear > maxYear) {
        newErrors.birthday = t('user.errors.birthday_too_young');
        isValid = false;
      } else if (birthYear === maxYear) {
        // Check if user has reached 18th birthday this year
        const eighteenthBirthday = new Date(birthDate);
        eighteenthBirthday.setFullYear(currentYear);

        if (currentDate < eighteenthBirthday) {
          newErrors.birthday = t('user.errors.birthday_too_young');
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    // Create API payload
    const payload = {
      nickname: formData.nickname,
      birthday: formData.birthday || null,
      gender: formData.gender || null,
      avatar: formData.avatar?.key || null,
    };

    // Pass the payload to parent component
    onSave(payload);
  };

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      
      // Validate image format only
      const validation = validateAvatar(file);
      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          avatar: validation.error
        }));
        setIsUploading(false);
        return;
      }

      // Get upload URL
      const fileInfo = {
        filename: encodeURIComponent(file.name),
        contentType: file.type,
        contentLength: file.size
      };
      
      const uploadData = await getAvatarUrl(fileInfo);

      const { url, method, image } = uploadData.data;
      
      // Upload file
      await uploadFile(url, method, file);
      
      // Update form data with new avatar URL
      setFormData(prev => ({
        ...prev,
        avatar: image
      }));
      
      // Clear any previous errors
      if (errors.avatar) {
        setErrors(prev => ({
          ...prev,
          avatar: ''
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        avatar: t('user.errors.upload_failed')
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenChangePassword = () => {
    setIsChangingPassword(true);
  };

  const handleOpenChangeEmail = () => {
    setIsChangingEmail(true);
  };

  const handleOpenBindPhone = () => {
    setIsBindingPhone(true);
  };

  // Format phone number for display
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    return `+${phone.countryCode} ${phone.nationalNumber}`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex flex-col justify-center items-end z-50">
      <div className="w-[480px] h-full bg-white flex flex-col justify-between items-start overflow-hidden">
        {/* Header */}
        <div className="self-stretch p-6 inline-flex items-center relative">
          <h2 className="text-lg font-bold">
              {t('user.editor.title')}
          </h2>
          <button 
            onClick={onClose}
            className="w-6 h-6 absolute right-6 top-6 flex items-center justify-center"
          >
            <img src={closeIcon} alt="close" className="w-7 h-7" />
          </button>
        </div>

        {/* Form Content */}
        <div className="self-stretch flex-1 px-6 pb-6 overflow-y-auto">
          <div className="self-stretch flex flex-col justify-start items-start gap-6">
            {/* Avatar Section */}
            <div className="self-stretch flex flex-col justify-start items-center gap-2">
              <div className="w-24 h-24 relative">
                <div className="w-24 h-24 bg-[#eeeeee] rounded-full flex items-center justify-center">
                  {formData.avatar ? (
                    <img 
                      src={formData.avatar.url || avatarIcon} 
                      alt="User Avatar" 
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <img src={avatarIcon} alt="User" className="w-24 h-24" />
                  )}
                </div>
              </div>
              {isUploading ? (
                <div className="text-sm text-gray-500">
                  {t('common.uploading')}...
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/jpeg,image/jpg,image/webp"
                    className="hidden"
                  />
                  <Button
                    variant="text"
                    size="s"
                    className="px-3.5 outline outline-1 outline-offset-[-1px] outline-[#dddddd] rounded-3xl"
                    onClick={handleAvatarClick}
                  >
                    {t('user.editor.change_avatar')}
                  </Button>
                  {errors.avatar && (
                    <div className="text-sm text-red-500 mt-1">
                      {errors.avatar}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Profile Form */}
            <div className="self-stretch flex flex-col justify-start items-start gap-3">
              {/* Username */}
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch inline-flex justify-start items-center gap-3">
                  <div className="self-stretch inline-flex justify-start items-center gap-1">
                    <div className="text-sm text-[#111111]">{t('user.editor.username')}</div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formData.nickname.length}/20
                  </div>
                </div>
                <BasicInput
                  placeholder={t('user.editor.username_placeholder')}
                  value={formData.nickname}
                  onChange={(e) => handleInputChange('nickname', e.target.value)}
                  containerClassName="self-stretch"
                  className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                  error={errors.nickname}
                />
              </div>

              {/* Phone Number */}
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="inline-flex justify-start items-center gap-1">
                    <div className="text-sm text-[#111111]">{t('user.editor.phone')}</div>
                  </div>
                  <div 
                    className="text-[#2966ff] text-sm font-medium cursor-pointer"
                    onClick={handleOpenBindPhone}
                  >
                    {profileData?.phone ? t('user.editor.change_phone') : t('user.editor.bind_phone')}
                  </div>
                </div>
                <BasicInput
                  value={profileData?.phone ? formatPhoneNumber(profileData.phone) : ""}
                  placeholder={t('user.editor.phone_placeholder')}
                  disabled={true}
                  containerClassName="self-stretch"
                />
              </div>

              {/* Email */}
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="inline-flex justify-start items-center gap-1">
                    <div className="text-sm text-[#111111]">{t('user.editor.email')}</div>
                  </div>
                  <div 
                    className="text-[#2966ff] text-sm font-medium cursor-pointer"
                    onClick={handleOpenChangeEmail}
                  >
                    {t('user.editor.change_email')}
                  </div>
                </div>
                <BasicInput
                  value={profileData?.email || "example@example.com"}
                  disabled={true}
                  containerClassName="self-stretch"
                />
              </div>

              {/* Birthday */}
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch inline-flex justify-start items-center gap-3">
                  <div className="self-stretch inline-flex justify-start items-center gap-1">
                    <div className="text-sm text-[#111111]">{t('user.editor.birthday')}</div>
                  </div>
                </div>
                <BasicInput
                  type="date"
                  placeholder="YYYY-MM-DD"
                  value={formData.birthday}
                  onChange={(e) => handleInputChange('birthday', e.target.value)}
                  containerClassName="self-stretch"
                  className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                  error={errors.birthday}
                  max={(() => {
                    const currentDate = new Date();
                    const maxDate = new Date(currentDate.getFullYear() - 18, currentDate.getMonth(), currentDate.getDate());
                    return maxDate.toISOString().split('T')[0];
                  })()}
                />
              </div>

              {/* Gender */}
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch inline-flex justify-start items-center gap-3">
                  <div className="self-stretch inline-flex justify-start items-center gap-1">
                    <div className="text-sm text-[#111111]">{t('user.editor.gender')}</div>
                  </div>
                </div>
                <Select
                  options={genderOptions}
                  value={formData.gender}
                  onValueChange={(value) => handleInputChange('gender', value)}
                  className="w-full h-12 text-start !rounded-md bg-[#f7f7f7]"
                  contentClassName="z-50"
                  placeholder={t('user.editor.gender_placeholder')}
                />
              </div>

              {/* Password */}
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="inline-flex justify-start items-center gap-1">
                    <div className="text-sm text-[#111111]">{t('user.editor.password')}</div>
                  </div>
                  <div 
                    className="text-[#2966ff] text-sm font-medium cursor-pointer" 
                    onClick={handleOpenChangePassword}
                  >
                    {t('user.editor.change_password')}
                  </div>
                </div>
                <BasicInput
                  type="password"
                  value="********"
                  disabled={true}
                  containerClassName="self-stretch"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Save Button */}
        <div className="w-[480px] px-6 py-4 bg-white inline-flex justify-center items-center gap-4">
          <Button
            onClick={onClose}
            variant="default"
            size="xl"
            className="flex-1 h-14 px-7 bg-white rounded-[28px] border border-[#dddddd]"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            size="xl"
            className="flex-1 h-14 px-7 bg-[#111111] rounded-[28px] flex justify-center items-center text-white"
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
      
      {/* Change Password Dialog */}
      {isChangingPassword && (
        <ChangePasswordDialog 
          onClose={() => setIsChangingPassword(false)}
        />
      )}

      {/* Change Email Dialog */}
      {isChangingEmail && (
        <ChangeEmailDialog 
          onClose={() => setIsChangingEmail(false)}
        />
      )}

      {/* Bind Phone Dialog */}
      {isBindingPhone && (
        <BindPhoneDialog 
          onClose={() => setIsBindingPhone(false)}
          phone={profileData?.phone}
        />
      )}
    </div>
  );
};

export default ProfileEditor; 